import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  createInvoiceApi,
  deleteInvoiceApi,
  editInvoiceApi,
  getInvoiceByIdApi,
  getInvoicePdfApi,
  getInvoicesApi,
  sendInvoiceEmailApi,
  updateStatusApi,
} from "helpers/ApiManager";

import { ApiResponse, PaginatedInvoiceSummaryPayload } from "types/ApiResponse";
import {
  CreateInvoiceArgs,
  InvoiceDetailsPayload,
  InvoicePayloadData,
  InvoicesQueryParams,
  UpdateInvoiceArgs,
} from "types/invoice.api";
import { StatusPayload } from "types/quote.api";
import { UpdateStatusApiArgs } from "types/setting.api";
import { ErrorHandler, SuccessToast, ToastFactory } from "utils/toast";

export const getInvoices = createAsyncThunk<
  ApiResponse<PaginatedInvoiceSummaryPayload<InvoicePayloadData>>, // return type
  InvoicesQueryParams | undefined, // argument type
  { rejectValue: string } // rejected type
>("invoices/getInvoices", async (params, { rejectWithValue }) => {
  try {
    const response = await getInvoicesApi(params);
    return response;
  } catch (err: any) {
    return rejectWithValue(err?.message || "Failed to fetch");
  }
});

export const getInvoiceById = createAsyncThunk<
  ApiResponse<InvoiceDetailsPayload>, // return type
  number, // argument type
  { rejectValue: string } // rejected type
>("invoices/getInvoiceById", async (invoiceId, { rejectWithValue }) => {
  try {
    const response = await getInvoiceByIdApi(invoiceId);
    return response;
  } catch (err: any) {
    return rejectWithValue(err?.message || "Failed to fetch quote by ID");
  }
});

export const createInvoice = createAsyncThunk<
  ApiResponse<InvoiceDetailsPayload>, // return type
  CreateInvoiceArgs, // argument type
  { rejectValue: string } // rejected type
>("invoices/createInvoice", async (params, { rejectWithValue }) => {
  try {
    const response = await createInvoiceApi(params);
    SuccessToast(response.message);

    return response;
  } catch (err: any) {
    return rejectWithValue(err?.message || "Failed to create invoice");
  }
});

export const editInvoice = createAsyncThunk<
  ApiResponse<InvoiceDetailsPayload>, // return type
  UpdateInvoiceArgs, // argument type
  { rejectValue: string } // rejected type
>("invoices/editInvoice", async (params, { rejectWithValue }) => {
  try {
    const response = await editInvoiceApi(params);
    console.log(response);
    SuccessToast(response.message);
    // console.log("🔥 editInvoice thunk fired");
    return response;
  } catch (err: any) {
    console.log(err);
    return rejectWithValue(err?.message || "Failed to edit invoice");
  }
});

export const deleteInvoice = createAsyncThunk<
  ApiResponse<number>, // return type
  number, // argument type
  { rejectValue: string } // rejected type
>("invoices/deleteInvoice", async (invoiceId, { rejectWithValue }) => {
  try {
    const response = await deleteInvoiceApi(invoiceId);
    response.payload = invoiceId;
    ToastFactory("Invoice", "Delete", true);
    return response;
  } catch (err: any) {
    ErrorHandler(err);
    return rejectWithValue(err?.message || "Failed to delete");
  }
});

export const updateInvoiceStatus = createAsyncThunk<
  ApiResponse<StatusPayload>, // return type
  UpdateStatusApiArgs, // argument type
  { rejectValue: string } // rejected type
>("invoices/updateStatus", async (params, { rejectWithValue }) => {
  try {
    const response = await updateStatusApi(params);
    response.payload = {
      invoice_id: params.invoice_id,
      status: params.status,
    };
    SuccessToast(response.message);
    return response;
  } catch (err: any) {
    ErrorHandler(err);
    return rejectWithValue(err?.message || "Failed to edit");
  }
});

export const getInvoicePdfPreview = createAsyncThunk<
  string, // return type: pdf preview html
  number, // argument type: invoiceId
  { rejectValue: string } // rejected type
>("invoices/getInvoicePdfPreview", async (invoiceId, { rejectWithValue }) => {
  try {
    const response = await getInvoicePdfApi({ invoiceId, is_download: 0 });
    return response as string;
  } catch (err: any) {
    return rejectWithValue(err?.message || "Failed to load invoice preview");
  }
});

export const sendInvoiceEmail = createAsyncThunk<
  ApiResponse<null>, // return type
  { invoiceId: number }, // argument type
  { rejectValue: string } // rejected type
>("invoices/sendInvoiceEmail", async (params, { rejectWithValue }) => {
  try {
    const response = await sendInvoiceEmailApi(params.invoiceId);
    console.log(response);
    SuccessToast(response.message);
    return response;
  } catch (err: any) {
    console.log(err);
    ErrorHandler(err);
    return rejectWithValue(err?.message || "Failed to edit quote");
  }
});
